import { MapBrowserEvent, Overlay } from 'ol';
import { Coordinate } from 'ol/coordinate';
import ImageLayer from 'ol/layer/Image';
import Layer from 'ol/layer/Layer';
import olMap from 'ol/Map';
import ImageWMS from 'ol/source/ImageWMS';
import { MapHighlightFeatures } from './MapHighlightFeatures';




export class MapFeatureInfo {

    private _overlay: Overlay = null;
    private _popupContainer: JQuery<HTMLElement> = null;
    private _popupContent: JQuery<HTMLElement> = null;
    private _popupCloser: JQuery<HTMLElement> = null;

    public constructor(private _mapHighlightFeatures: MapHighlightFeatures, private _bufferGetFeatureInfo: boolean) {

    }

    public showFeaturesAtEvent = (
        map: olMap,
        evt: MapBrowserEvent,
        callback: Function): void => {
        this.requestFeatureInfo(map, evt.coordinate, callback);
    }

    public showFeaturesAtCoordinate = (
        map: olMap,
        coordinate: Coordinate,
        callback: Function): void => {
        this.requestFeatureInfo(map, coordinate, callback);
    }

    public showFeatureInfoForLayers = (
        map: olMap,
        coordinate: Coordinate,
        layerNames: Array<string>,
        callback: Function = function () { }): void => {
        this.requestFeatureInfo(map, coordinate, callback, layerNames);
    }

    /** Shows arbitrary HTML in the info popup at a coordinate (e.g. login / expiry prompts). */
    public showHtmlAtCoordinate = (
        map: olMap,
        coordinate: Coordinate,
        html: string): void => {
        this.showPopup(map, html, coordinate);
    }

    public closePopup = (): void => {
        if (this._overlay) {
            this._overlay.setPosition(undefined);
        }
        if (this._popupContainer) {
            this._popupContainer.css("display", "none");
        }
        this._mapHighlightFeatures.clear();
    }

    private requestFeatureInfo = (
        map: olMap,
        coordinate: Coordinate,
        callback: Function,
        forcedLayerNames: Array<string> = null): void => {
        let self = this;
        let layerNames: Array<string> = [];
        let viewResolution: number = map.getView().getResolution();
        let wmsLayer: ImageLayer;

        if (forcedLayerNames && forcedLayerNames.length > 0) {
            forcedLayerNames.forEach(function (layerName) {
                let lyr = self.getImageLayerByName(map, layerName);
                if (lyr && lyr.getVisible()) {
                    layerNames.push(layerName);
                    if (!wmsLayer) {
                        wmsLayer = lyr;
                    }
                }
            });
        } else {
            // Helper to safely check if a named WMS image layer is currently visible.
            const isLayerVisibleByName = (layerName: string): boolean => {
                return !!self.getImageLayerByName(map, layerName)?.getVisible();
            };

            const isBoatVisible = isLayerVisibleByName("BOATLines");
            const isProwVisible = isLayerVisibleByName("PROW-Byway");

            // Only include PROW in info-click requests when either PROW itself
            // or BOAT is visible. This keeps PROW queries aligned with map state.
            map.getLayers().forEach(function (lyr: Layer) {
                if (lyr instanceof ImageLayer) {

                    let sourceParams = (<ImageWMS>lyr.getSource()).getParams();
                    let layerName = sourceParams["LAYERS"];
                    let isProwLayer = layerName === "PROW-Byway";
                    let includeProwLayer = isProwLayer && (isProwVisible || isBoatVisible);
                   
                    if ((lyr.getVisible() && lyr.getProperties().queryable !== false) || includeProwLayer) {

                        let minRes = lyr.getMaxResolution()
                        let maxRes = lyr.getMinResolution()

                        if (minRes > viewResolution && maxRes <= viewResolution) {
                            layerNames.push(sourceParams.LAYERS);
                            wmsLayer = lyr;
                        }
                    }
                }
            });
        }

        if (layerNames.length > 0 && wmsLayer) {

            if (this._bufferGetFeatureInfo && viewResolution < 4.804877096575387 && !forcedLayerNames) {
                viewResolution = 4.804877096575387;
            }

            let wmsUrl = (<ImageWMS>wmsLayer.getSource()).getFeatureInfoUrl(
                coordinate,
                viewResolution,
                map.getView().getProjection().getCode(),
                {
                    'INFO_FORMAT': 'text/html'
                }
            );

            let updateUrlParameter = function (infoUrl, param, value) {
                let regex = new RegExp('(&' + param + '=)[^\\&]+');
                return infoUrl.replace(regex, '$1' + value);
            }

            wmsUrl = updateUrlParameter(wmsUrl, "QUERY_LAYERS", layerNames.join());
            wmsUrl = updateUrlParameter(wmsUrl, "LAYERS", layerNames.join());

            $("#mapLoader").show();
            $.get(wmsUrl, function (html) {
                self.showPopup(map, html, coordinate);
            }).always(function () {
                $("#mapLoader").hide("slow");
                if (callback) {
                    callback.call(self);
                }
            });
        } else {
            if (callback) {
                callback.call(self);
            }
        }
    }

    private getImageLayerByName = (map: olMap, layerName: string): ImageLayer => {
        let found: ImageLayer = null;
        map.getLayers().forEach(function (lyr: Layer) {
            if (found) {
                return;
            }
            if (lyr instanceof ImageLayer) {
                let params = (<ImageWMS>lyr.getSource()).getParams();
                if (params.LAYERS === layerName) {
                    found = lyr;
                }
            }
        });
        return found;
    }

    private showPopup = (map: olMap, html: string, coordinates: Coordinate): void => {
        let self = this;
        let mapDiv = $("#map");

        if (self._popupContainer === null) {
            self._popupContainer = $("<div>", { id: "popup", class: "ol-popup" });
            self._popupCloser = $("<div>", { id: "popup-closer", class: "ol-popup-closer", href: "#" });
            self._popupContent = $("<div>", { id: "popup-content", class: "ol-popup-content" });

            mapDiv.append(self._popupContainer);
            self._popupContainer.append(self._popupCloser);
            self._popupContainer.append(self._popupContent);
            
            //Add a click handler to hide the popup.
            self._popupCloser.on("click", function () {
                //console.log('close clicked');
                self._overlay.setPosition(undefined);
                self._popupContainer.css("display", "none");
                self._popupCloser.blur();
                self._mapHighlightFeatures.clear();
                return false;
            });

        }
        if (self._overlay === null) {

            self._overlay = new Overlay({
                autoPan: true,
                element: self._popupContainer[0]
            });
            map.addOverlay(self._overlay);
        }

        if (html) {
            self._popupContent.html(html);

            //look for duplicate data-route-id
            let ids = new Array<string>();
            self._popupContent.find("[data-route-id]").each(function (index, element) {
                const id = $(this).data("routeId");

                if (ids.includes(id)) {
                    $(this).css("display", "none");
                } else {
                    ids.push(id);
                }                
            });

            self._popupContainer.css("display", "block");
            if (coordinates) {
                self._overlay.setPosition(coordinates);
            }
        }
    }

}
